/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useParams, Link } from 'react-router-dom';
import { SEO, Tag, ButtonLink, ClockIcon } from '../components/common';
import { BlogCard, AuthorCard } from '../components/blog';
import { getPostBySlug, getRelatedPosts } from '../data/blogPosts';
import { useScrollProgress } from '../hooks/useScrollProgress';
import { colors, borderRadius, shadows, typography } from '../styles/theme';

const progressBarStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, ${colors.primary[400]}, ${colors.primary[600]});
  z-index: 200;
  transition: width 50ms linear;
`;

const pageStyles = css`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem 0;

  @media (min-width: 768px) {
    padding: 3rem 2rem 0;
  }
`;

const breadcrumbStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.muted};

  a {
    color: ${colors.text.secondary};
    text-decoration: none;

    &:hover {
      color: ${colors.primary[700]};
    }
  }
`;

const headerStyles = css`
  margin-bottom: 2rem;
`;

const categoryStyles = css`
  margin-bottom: 1rem;
`;

const titleStyles = css`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.extrabold};
  color: ${colors.text.primary};
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    font-size: ${typography.fontSize['4xl']};
  }
`;

const metaStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const authorMetaStyles = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  img {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: ${borderRadius.full};
    object-fit: cover;
  }

  .info {
    .name {
      font-weight: ${typography.fontWeight.semibold};
      color: ${colors.text.primary};
    }

    .date {
      font-size: ${typography.fontSize.sm};
      color: ${colors.text.muted};
    }
  }
`;

const readTimeStyles = css`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const coverImageStyles = css`
  width: 100%;
  aspect-ratio: 2 / 1;
  object-fit: cover;
  background: ${colors.neutral[100]};
  border-radius: ${borderRadius.xl};
  margin-bottom: 2.5rem;
  box-shadow: ${shadows.lg};
`;

const contentStyles = css`
  font-size: 1.0625rem;
  line-height: 1.8;
  color: ${colors.neutral[600]};

  @media (min-width: 768px) {
    font-size: ${typography.fontSize.lg};
  }

  h2, h3, h4 {
    color: ${colors.text.primary};
    margin-bottom: 1rem;
  }

  /* İçerik başlıkla başlıyorsa kapak görselinin altında fazladan boşluk olmasın */
  * + h2, * + h4 { margin-top: 2.5rem; }
  * + h3 { margin-top: 2rem; }

  h2 { font-size: ${typography.fontSize['2xl']}; }
  h3 { font-size: ${typography.fontSize.xl}; }
  h4 { font-size: ${typography.fontSize.lg}; }

  p {
    color: inherit;
    margin-bottom: 1.5rem;
  }

  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      padding-left: 0.25rem;
      list-style: disc;
    }

    li::marker {
      color: ${colors.primary[500]};
    }
  }

  ol li {
    list-style: decimal;
  }

  ol li::marker {
    font-weight: ${typography.fontWeight.bold};
  }

  strong {
    color: ${colors.text.primary};
    font-weight: ${typography.fontWeight.semibold};
  }

  a {
    color: ${colors.primary[700]};
    text-decoration: underline;

    &:hover {
      color: ${colors.primary[700]};
    }
  }

  hr {
    border: none;
    border-top: 1px solid ${colors.neutral[200]};
    margin: 2rem 0;
  }

  blockquote {
    border-left: 4px solid ${colors.primary[500]};
    padding-left: 1.5rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: ${colors.text.secondary};
  }
`;

const tagsStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2rem 0;
  padding: 1.5rem 0;
  border-top: 1px solid ${colors.neutral[200]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const authorSectionStyles = css`
  margin: 2rem 0 0;
`;

// İlgili yazılar makaleden daha geniş alanda gösterilir, kartlar sıkışmasın
const relatedSectionStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1rem 3rem;

  @media (min-width: 768px) {
    padding: 5rem 2rem 4rem;
  }

  h2 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    color: ${colors.text.primary};
    margin-bottom: 1.5rem;
  }
`;

const relatedGridStyles = css`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const notFoundStyles = css`
  text-align: center;
  padding: 5rem 1rem;

  h1 {
    font-size: ${typography.fontSize['3xl']};
    color: ${colors.text.primary};
    margin-bottom: 1rem;
  }

  p {
    color: ${colors.text.secondary};
    margin-bottom: 2rem;
  }
`;

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function renderInline(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

const BLOCK_START = /^(#{1,4}\s|[-*]\s|\d+\.\s|-{3,}$|>\s)/;

// Yazı içeriğindeki basit markdown'ı HTML'e çevirir. İçerikteki ilk "# Başlık"
// sayfa başlığıyla aynı olduğu için atlanır.
function renderMarkdown(content: string): string {
  const lines = content.trim().replace(/^# .*(\r?\n)+/, '').split(/\r?\n/).map(line => line.trim());
  const blocks: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line) {
      i++;
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = Math.min(Math.max(heading[1].length, 2), 4);
      blocks.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    if (/^-{3,}$/.test(line)) {
      blocks.push('<hr />');
      i++;
      continue;
    }

    const listMarker = /^([-*]|\d+\.)\s+/.exec(line);
    if (listMarker) {
      const ordered = /\d/.test(listMarker[1]);
      const itemPattern = ordered ? /^\d+\.\s+(.*)$/ : /^[-*]\s+(.*)$/;
      const items: string[] = [];
      while (i < lines.length) {
        const item = itemPattern.exec(lines[i]);
        if (!item) break;
        items.push(`<li>${renderInline(item[1])}</li>`);
        i++;
      }
      const tag = ordered ? 'ol' : 'ul';
      blocks.push(`<${tag}>${items.join('')}</${tag}>`);
      continue;
    }

    if (line.startsWith('> ')) {
      blocks.push(`<blockquote>${renderInline(line.slice(2))}</blockquote>`);
      i++;
      continue;
    }

    const paragraph: string[] = [];
    while (i < lines.length && lines[i] && (paragraph.length === 0 || !BLOCK_START.test(lines[i]))) {
      paragraph.push(lines[i]);
      i++;
    }
    blocks.push(`<p>${renderInline(paragraph.join(' '))}</p>`);
  }

  return blocks.join('\n');
}

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const progress = useScrollProgress();

  if (!post) {
    return (
      <div css={notFoundStyles}>
        <SEO title="Yazı Bulunamadı" description="Aradığınız blog yazısı bulunamadı." noIndex />
        <h1>Yazı Bulunamadı</h1>
        <p>Aradığınız blog yazısı mevcut değil veya kaldırılmış olabilir.</p>
        <ButtonLink to="/blog">Blog'a Dön</ButtonLink>
      </div>
    );
  }

  const relatedPosts = getRelatedPosts(post, 3);

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={post.tags}
        image={post.coverImage}
        url={`/blog/${post.slug}`}
        type="article"
        publishedTime={post.publishedAt}
        author={post.author.name}
      />

      <div css={progressBarStyles} style={{ width: `${progress}%` }} aria-hidden="true" />
      <article css={pageStyles}>
        <nav css={breadcrumbStyles} aria-label="Sayfa konumu">
          <Link to="/">Ana Sayfa</Link>
          <span aria-hidden="true">/</span>
          <Link to="/blog">Blog</Link>
          <span aria-hidden="true">/</span>
          <Link to={`/kategori/${post.category.slug}`}>{post.category.name}</Link>
        </nav>

        <header css={headerStyles}>
          <div css={categoryStyles}>
            <Tag label={post.category.name} color={post.category.color} size="md" />
          </div>

          <h1 css={titleStyles}>{post.title}</h1>

          <div css={metaStyles}>
            <div css={authorMetaStyles}>
              <img src={post.author.avatar} alt="" />
              <div className="info">
                <div className="name">{post.author.name}</div>
                <time className="date" dateTime={post.publishedAt}>
                  {formatDate(post.publishedAt)}
                </time>
              </div>
            </div>
            <div css={readTimeStyles}>
              <ClockIcon />
              <span>{post.readTime} dk okuma</span>
            </div>
          </div>
        </header>

        <img css={coverImageStyles} src={post.coverImage} alt="" />

        <div
          css={contentStyles}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
        />

        <div css={tagsStyles}>
          {post.tags.map(tag => (
            <Tag key={tag} label={`#${tag}`} />
          ))}
        </div>

        <div css={authorSectionStyles}>
          <AuthorCard author={post.author} />
        </div>
      </article>

      {relatedPosts.length > 0 && (
        <section css={relatedSectionStyles}>
          <h2>İlgili Yazılar</h2>
          <div css={relatedGridStyles}>
            {relatedPosts.map(relatedPost => (
              <BlogCard key={relatedPost.id} post={relatedPost} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
